export type DisplayType =
  | "Residential"
  | "Commercial"
  | "Non-Profit"
  | "Community/Neighborhood"
  | "Municipal/Government"
  | "Theme Park/Attraction"

export const DISPLAY_TYPES: DisplayType[] = [
  "Residential",
  "Commercial",
  "Non-Profit",
  "Community/Neighborhood",
  "Municipal/Government",
  "Theme Park/Attraction",
]

export type ExperienceType =
  | "Drive-Through"
  | "Walk-Through"
  | "Drive-By/Curbside"
  | "Interactive"
  | "Static Display"
  | "Animated/Synchronized"
  | "Mega Display"

export const EXPERIENCE_TYPES: ExperienceType[] = [
  "Drive-Through",
  "Walk-Through",
  "Drive-By/Curbside",
  "Interactive",
  "Static Display",
  "Animated/Synchronized",
  "Mega Display",
]

export const HOLIDAY_THEMES = [
  "Christmas",
  "Halloween",
  "Hanukkah",
  "Diwali",
  "New Year",
  "Winter Wonderland",
  "Multi-Holiday",
] as const

export const ENTERTAINMENT_FEATURES = [
  "Music Synchronized",
  "FM Radio Broadcast",
  "Live Performances",
  "Character Appearances",
  "Photo Opportunities",
] as const

export const ACCESSIBILITY_FEATURES = [
  "Wheelchair Accessible",
  "Parking Available",
  "Public Transit Nearby",
  "Restrooms Available",
  "Food/Beverages Available",
] as const

export const TECHNICAL_FEATURES = [
  "Sequence Downloads Available",
  "RGB Pixels",
  "Traditional Lights",
  "Projection Mapping",
] as const

export type Admission = "Free" | "Ticketed"

export type Display = {
  id: string
  name: string
  type: DisplayType
  experience: ExperienceType[]
  holidays: (typeof HOLIDAY_THEMES)[number][]
  features: {
    entertainment: (typeof ENTERTAINMENT_FEATURES)[number][]
    accessibility: (typeof ACCESSIBILITY_FEATURES)[number][]
    technical: (typeof TECHNICAL_FEATURES)[number][]
  }
  songsCount?: number
  schedule: {
    startDate: string
    endDate: string
    opensAt?: string // "17:00"
    closesAt?: string // "22:00"
    openWeekdays?: boolean
    openWeekends?: boolean
    openHolidays?: boolean
  }
  location: { lat: number; lng: number }
  city?: string
  state?: string
  avgRating?: number
  reviewCount?: number
  verified?: boolean
  awards?: boolean
  charity?: boolean
  admission?: Admission
  popularity?: number
  addedAt?: string
  updatedAt?: string
  _distanceMi?: number
}

export type SortBy =
  | "relevance"
  | "distance"
  | "popularity"
  | "rating"
  | "recentlyAdded"
  | "recentlyUpdated"
  | "openingSoon"
  | "songCount"
  | "alphaAsc"
  | "alphaDesc"

export const SORT_OPTIONS: { value: SortBy; label: string }[] = [
  { value: "relevance", label: "Relevance" },
  { value: "distance", label: "Distance (nearest)" },
  { value: "popularity", label: "Popularity" },
  { value: "rating", label: "Rating" },
  { value: "recentlyAdded", label: "Recently Added" },
  { value: "recentlyUpdated", label: "Recently Updated" },
  { value: "openingSoon", label: "Opening Soon" },
  { value: "songCount", label: "Song Count" },
  { value: "alphaAsc", label: "Alphabetical (A–Z)" },
  { value: "alphaDesc", label: "Alphabetical (Z–A)" },
]

export type SecondarySort =
  | "ratingDesc"
  | "distanceAsc"
  | "songCountDesc"
  | "alphaAsc"
  | "alphaDesc"

export const SecondarySortOptions: { value: SecondarySort; label: string }[] = [
  { value: "ratingDesc", label: "Rating (desc)" },
  { value: "distanceAsc", label: "Distance (asc)" },
  { value: "songCountDesc", label: "Song Count (desc)" },
  { value: "alphaAsc", label: "A–Z" },
  { value: "alphaDesc", label: "Z–A" },
]

export type FiltersState = {
  query: string
  types: DisplayType[]
  experiences: ExperienceType[]
  holidays: (typeof HOLIDAY_THEMES)[number][]
  entertainment: (typeof ENTERTAINMENT_FEATURES)[number][]
  accessibility: (typeof ACCESSIBILITY_FEATURES)[number][]
  technical: (typeof TECHNICAL_FEATURES)[number][]
  songsRange: [number, number]
  dateFrom?: string
  dateTo?: string
  opensBefore?: string
  closesAfter?: string
  openWeekdays: boolean
  openWeekends: boolean
  openHolidays: boolean
  currentlyActive: boolean
  hasReviews: boolean
  minRating: number
  verified: boolean
  awards: boolean
  charity: boolean
  admission: "any" | "free" | "ticketed"
  radiusMiles: number
  sortBy: SortBy
  secondarySort: SecondarySort
}

export const defaultFiltersState: FiltersState = {
  query: "",
  types: [],
  experiences: [],
  holidays: [],
  entertainment: [],
  accessibility: [],
  technical: [],
  songsRange: [0, 100],
  dateFrom: undefined,
  dateTo: undefined,
  opensBefore: undefined,
  closesAfter: undefined,
  openWeekdays: false,
  openWeekends: false,
  openHolidays: false,
  currentlyActive: false,
  hasReviews: false,
  minRating: 0,
  verified: false,
  awards: false,
  charity: false,
  admission: "any",
  radiusMiles: 25,
  sortBy: "relevance",
  secondarySort: "ratingDesc",
}
